// Approval requests per organisation (pending inbox + a history long enough to page).
// Approve / reject / cancel transition the request; approving a `user.create`
// request executes it — the person is added to the organisation's users.

import {
  DEMO_DRIVERS,
  DEMO_MATERIAL_IDS,
  DEMO_OUTLET_ORG_ID,
  DEMO_OUTLET_STAFF,
  DEMO_RECYCLER_MANAGERS,
  DEMO_RECYCLER_ORG_ID,
  DEMO_SITES,
  DEMO_USER,
  HOUR_MS,
  ids,
  iso,
  materialById,
} from '../core';
import type {
  ApprovalMode,
  ApprovalRequestSchema,
  ApprovalRequestStep,
  ApprovalStatus,
  SubmitApprovalPayload,
} from '../../types/approval.type';
import { allPickupOrders, orderKg } from './pickupOrders';
import { addApprovedUser, usernameOf } from './users';

export class DemoApprovalError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

type StepSeed = { perm: string; status: 'pending' | 'approved' | 'rejected'; by?: string; notes?: string; afterH?: number };

interface Seed {
  action: string;
  entityId: string;
  payload: Record<string, unknown>;
  justification: string;
  by: string;
  hoursAgo: number;
  status: ApprovalStatus;
  mode?: ApprovalMode;
  steps?: StepSeed[];
  resolvedBy?: string;
  resolvedAfterH?: number;
  rejection?: string;
}

const perm = (orgId: string, entity: string, action: string) => `recyclee.${orgId}.*.${entity}.${action}`;

function approversFor(orgId: string): string[] {
  const admins =
    orgId === DEMO_OUTLET_ORG_ID
      ? [DEMO_OUTLET_STAFF[0].email]
      : [DEMO_RECYCLER_MANAGERS[0].email];
  return [DEMO_USER.username, ...admins];
}

function build(orgId: string, n: number, seed: Seed): ApprovalRequestSchema {
  const id = ids.approval(n);
  const requestedAt = Date.now() - seed.hoursAgo * HOUR_MS;
  const mode = seed.mode ?? 'requires_approval';
  const stepsSeed = seed.steps ?? (mode === 'justified' ? [] : [{ perm: perm(orgId, 'approval', 'write'), status: 'pending' as const }]);
  const steps: ApprovalRequestStep[] = stepsSeed.map((s, i) => {
    const actedAt = s.status !== 'pending' ? iso(new Date(requestedAt + (s.afterH ?? 3 + i * 5) * HOUR_MS)) : null;
    return {
      id: ids.approval(10000 + n * 10 + i + 1),
      step_order: i + 1,
      required_permission: s.perm,
      status: s.status,
      acted_by: s.status !== 'pending' ? s.by ?? DEMO_USER.id : null,
      acted_by_username: s.status !== 'pending' ? usernameOf(s.by ?? DEMO_USER.id) : null,
      acted_at: actedAt,
      notes: s.notes ?? null,
      eligible_approvers: approversFor(orgId),
    };
  });
  const pendingStep = steps.find((s) => s.status === 'pending');
  const resolved = seed.status !== 'pending';
  const resolvedAt = resolved ? iso(new Date(requestedAt + (seed.resolvedAfterH ?? 6) * HOUR_MS)) : null;
  const resolvedBy = resolved ? seed.resolvedBy ?? (mode === 'justified' ? null : DEMO_USER.id) : null;
  return {
    id,
    scope: 'organization',
    organization_id: orgId,
    mode,
    status: seed.status,
    action_type: seed.action,
    entity_type: seed.action.split('.')[0],
    entity_id: seed.entityId,
    payload: seed.payload,
    justification: seed.justification,
    requested_by: seed.by,
    requested_by_username: usernameOf(seed.by),
    requested_at: iso(new Date(requestedAt)),
    resolved_by: resolvedBy,
    resolved_by_username: usernameOf(resolvedBy),
    resolved_at: resolvedAt,
    rejection_reason: seed.rejection ?? null,
    executed_at: seed.status === 'executed' ? iso(new Date(requestedAt + ((seed.resolvedAfterH ?? 6) + 0.1) * HOUR_MS)) : null,
    current_step_order: pendingStep ? pendingStep.step_order : steps.length ? steps[steps.length - 1].step_order : null,
    data: {},
    steps,
    created_at: iso(new Date(requestedAt)),
    updated_at: resolvedAt ?? iso(new Date(requestedAt)),
  };
}

interface OrderPick {
  id: string;
  reference: string;
  site: string;
  city: string;
  material: string;
  kg: number;
  payload: Record<string, unknown>;
}

interface PickFilter {
  /** Index into DEMO_SITES. */
  site?: number;
  /** Catalog material code (DEMO_MATERIAL_IDS key). */
  material?: string;
  city?: 'Riyadh' | 'Jeddah';
  truck?: number;
}

const round5 = (kg: number) => Math.max(5, Math.round(kg / 5) * 5);
const kgText = (kg: number) => `${Math.round(kg).toLocaleString('en-US')} kg`;

/**
 * The `nth` newest order with `status` matching the filter. Filters relax (material,
 * then site/city/truck) when nothing matches, and every text is built from the order
 * actually picked — so justifications always name that order's branch and material.
 */
function pickOrder(status: 'completed' | 'pending' | 'in_transit', filter: PickFilter = {}, nth = 0): OrderPick {
  const all = allPickupOrders()
    .filter((o) => o.status === status)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const matches = (o: (typeof all)[number], f: PickFilter) => {
    const site = DEMO_SITES.find((s) => s.id === o.site_id);
    return (
      (f.site === undefined || o.site_id === DEMO_SITES[f.site]?.id) &&
      (f.material === undefined || o.material_id === DEMO_MATERIAL_IDS[f.material]) &&
      (f.city === undefined || site?.city === f.city) &&
      (f.truck === undefined || o.truck_id === ids.truck(f.truck))
    );
  };
  const relaxed: PickFilter = { ...filter, material: undefined };
  const pool = [all.filter((o) => matches(o, filter)), all.filter((o) => matches(o, relaxed)), all].find((l) => l.length > 0) ?? all;
  const o = pool[nth % pool.length];
  const site = DEMO_SITES.find((s) => s.id === o.site_id);
  const material = materialById(o.material_id)?.name_en ?? 'Recyclables';
  const kg = orderKg(o);
  return {
    id: o.id,
    reference: o.reference,
    site: site?.name_en ?? 'the branch',
    city: site?.city ?? 'Riyadh',
    material,
    kg,
    payload: { reference: o.reference, collection_site: site?.name_en ?? null, city: site?.city ?? null, material, amount: kg, amount_unit: 'kg' },
  };
}

function outletSeeds(): Seed[] {
  const O = DEMO_OUTLET_ORG_ID;
  const S = DEMO_OUTLET_STAFF.map((s) => s.id);
  const me = DEMO_USER.id;
  // Requesters raise requests for their own branches (core staff site assignments).
  const weighOlaya = pickOrder('completed', { site: 0 });
  const dupPending = pickOrder('pending');
  const rawdahRollback = pickOrder('completed', { site: 4 });
  const malqaFix = pickOrder('completed', { site: 1 });
  const salamahTypo = pickOrder('completed', { site: 5 });
  const dcPallets = pickOrder('completed', { site: 3, material: 'wood_pallets' });
  const nasimDelete = pickOrder('completed', { site: 2 });
  const salamahLow = pickOrder('completed', { site: 5 }, 1);
  const olayaWet = pickOrder('completed', { site: 0, material: 'occ' }, 1);
  const testOrder = pickOrder('pending', {}, 3);
  const rawdahScale = pickOrder('completed', { site: 4 }, 2);
  const weighTo = round5(weighOlaya.kg * 0.9);
  const malqaTo = round5(malqaFix.kg * 0.92);
  const lowTo = round5(salamahLow.kg * 1.12);
  const scaleTo = round5(rawdahScale.kg * 1.04);
  return [
    // ── Pending inbox ──
    {
      action: 'user.create', entityId: ids.user(901), status: 'pending', by: S[0], hoursAgo: 46,
      payload: { username: 'l.alqahtani@alnakheel.example', first_name: 'Lama', last_name: 'Al-Qahtani', gender: 'Female', nationality: 'Saudi Arabia', phone: '+966500010188', role: 'staff', collection_site_ids: [DEMO_SITES[0].id] },
      justification: `Lama joins ${DEMO_SITES[0].name_en} as night-shift waste coordinator from Sunday and needs to raise pickups for the cardboard compactor.`,
      steps: [{ perm: perm(O, 'user', 'write'), status: 'pending' }],
    },
    {
      action: 'pickup_order.update_sensitive', entityId: weighOlaya.id, status: 'pending', by: S[3], hoursAgo: 20,
      payload: { ...weighOlaya.payload, corrected_amount: weighTo },
      justification: `Weighbridge ticket WB-48213 for ${weighOlaya.reference} (${weighOlaya.material}, ${weighOlaya.site}) shows ${kgText(weighTo)}; the driver entered ${kgText(weighOlaya.kg)}.`,
      steps: [
        { perm: perm(O, 'pickup_order', 'write'), status: 'approved', by: S[1], notes: 'Ticket checked against the gate log.', afterH: 4 },
        { perm: perm(O, 'approval', 'write'), status: 'pending' },
      ],
    },
    {
      action: 'pickup_order.delete', entityId: dupPending.id, status: 'pending', by: me, hoursAgo: 5,
      payload: dupPending.payload,
      justification: `Duplicate request: ${dupPending.site} raised the same ${dupPending.material} pickup twice (${dupPending.reference}).`,
    },
    {
      action: 'pickup_order.rollback_after_pickup', entityId: rawdahRollback.id, status: 'pending', by: S[7], hoursAgo: 70,
      payload: rawdahRollback.payload,
      justification: `The truck left ${rawdahRollback.site} without loading the ${rawdahRollback.material}; ${rawdahRollback.reference} has to be reopened so it is collected on the next run.`,
    },
    // ── History ──
    {
      action: 'user.create', entityId: S[11], status: 'executed', by: S[1], hoursAgo: 24 * 41, resolvedAfterH: 5,
      payload: { username: DEMO_OUTLET_STAFF[11].email, first_name: 'Rahul', last_name: 'Menon', role: 'staff', collection_site_ids: [DEMO_SITES[3].id] },
      justification: `Logistics clerk for ${DEMO_SITES[3].name_en}; replaces the temporary agency account.`,
      steps: [{ perm: perm(O, 'user', 'write'), status: 'approved', by: me, notes: 'Welcome aboard.', afterH: 5 }],
    },
    {
      action: 'pickup_order.update_sensitive', entityId: malqaFix.id, status: 'executed', by: S[4], hoursAgo: 24 * 9, resolvedBy: S[0], resolvedAfterH: 9,
      payload: { ...malqaFix.payload, corrected_amount: malqaTo },
      justification: `The scale ticket for ${malqaFix.reference} at ${malqaFix.site} confirms ${kgText(malqaTo)} of ${malqaFix.material}, not ${kgText(malqaFix.kg)}.`,
      steps: [
        { perm: perm(O, 'pickup_order', 'write'), status: 'approved', by: S[1], afterH: 2 },
        { perm: perm(O, 'approval', 'write'), status: 'approved', by: S[0], notes: 'Matches the recycler receipt.', afterH: 9 },
      ],
    },
    {
      action: 'pickup_order.update_sensitive', entityId: salamahTypo.id, status: 'executed', mode: 'justified', by: S[2], hoursAgo: 24 * 6, resolvedAfterH: 0.1,
      payload: { ...salamahTypo.payload, corrected_amount: salamahTypo.kg },
      justification: `Typo fix on ${salamahTypo.reference} (${salamahTypo.site}): ${kgText(salamahTypo.kg / 10)} was entered instead of ${kgText(salamahTypo.kg)} of ${salamahTypo.material}.`,
    },
    {
      action: 'pickup_order.rollback_after_pickup', entityId: dcPallets.id, status: 'approved', by: S[6], hoursAgo: 24 * 4, resolvedAfterH: 3,
      payload: dcPallets.payload,
      justification: `The ${dcPallets.material} load from ${dcPallets.site} (${dcPallets.reference}) was turned back at the gate as damaged; reopen it so it can be re-sorted and sent again.`,
      steps: [{ perm: perm(O, 'approval', 'write'), status: 'approved', by: me, afterH: 3 }],
    },
    {
      action: 'pickup_order.delete', entityId: nasimDelete.id, status: 'rejected', by: S[5], hoursAgo: 24 * 12, resolvedBy: S[1], resolvedAfterH: 7,
      payload: nasimDelete.payload,
      rejection: 'The order was already collected and weighed; deleting it would remove a delivery from the ESG totals. Submit a weight correction instead.',
      justification: `The wrong ${nasimDelete.site} container was selected when ${nasimDelete.reference} was raised.`,
      steps: [{ perm: perm(O, 'approval', 'write'), status: 'rejected', by: S[1], afterH: 7 }],
    },
    {
      action: 'user.create', entityId: ids.user(902), status: 'rejected', by: S[9], hoursAgo: 24 * 16, resolvedAfterH: 20,
      payload: { username: 'temp.cashier@alnakheel.example', first_name: 'Temporary', last_name: 'Cashier', role: 'staff', collection_site_ids: [DEMO_SITES[1].id] },
      rejection: 'Seasonal staff should use the shared branch login until their contract is signed.',
      justification: `Seasonal cashier needs to log cardboard bales at ${DEMO_SITES[1].name_en}.`,
      steps: [{ perm: perm(O, 'user', 'write'), status: 'rejected', by: me, afterH: 20 }],
    },
    {
      action: 'pickup_order.update_sensitive', entityId: salamahLow.id, status: 'cancelled', by: S[8], hoursAgo: 24 * 7, resolvedBy: S[8], resolvedAfterH: 1.5,
      payload: { ...salamahLow.payload, corrected_amount: lowTo },
      justification: `The weight on ${salamahLow.reference} looked low for a full ${salamahLow.material} load at ${salamahLow.site}.`,
    },
    {
      action: 'pickup_order.reject', entityId: olayaWet.id, status: 'executed', by: S[3], hoursAgo: 24 * 22, resolvedBy: S[0], resolvedAfterH: 4,
      payload: olayaWet.payload,
      justification: `The recycler reported the ${olayaWet.material} from ${olayaWet.site} (${olayaWet.reference}) as rain-soaked on arrival; accept their rejection so the order closes without a sale.`,
      steps: [{ perm: perm(O, 'approval', 'write'), status: 'approved', by: S[0], notes: 'Photos from the plant attached to the ticket.', afterH: 4 }],
    },
    {
      action: 'user.create', entityId: ids.user(903), status: 'cancelled', by: me, hoursAgo: 24 * 27, resolvedBy: me, resolvedAfterH: 2,
      payload: { username: 'auditor@alnakheel.example', first_name: 'External', last_name: 'Auditor', role: 'admin_read_only' },
      justification: 'Read-only access for the external ESG auditor.',
    },
    {
      action: 'pickup_order.delete', entityId: testOrder.id, status: 'executed', mode: 'justified', by: S[0], hoursAgo: 24 * 33, resolvedAfterH: 0.1,
      payload: testOrder.payload,
      justification: `Test request (${testOrder.material}, ${testOrder.site}) created during branch onboarding.`,
    },
    {
      action: 'pickup_order.update_sensitive', entityId: rawdahScale.id, status: 'executed', by: S[7], hoursAgo: 24 * 52, resolvedBy: S[2], resolvedAfterH: 11,
      payload: { ...rawdahScale.payload, corrected_amount: scaleTo },
      justification: `${rawdahScale.city} gate scale reading (${kgText(scaleTo)} of ${rawdahScale.material}) added to ${rawdahScale.reference} after the driver closed the order.`,
      steps: [{ perm: perm(O, 'approval', 'write'), status: 'approved', by: S[2], afterH: 11 }],
    },
  ];
}

function recyclerSeeds(): Seed[] {
  const R = DEMO_RECYCLER_ORG_ID;
  const M = DEMO_RECYCLER_MANAGERS.map((m) => m.id);
  const D = DEMO_DRIVERS.map((d) => d.id);
  const me = DEMO_USER.id;
  const sulayScale = pickOrder('completed', { city: 'Riyadh' }, 1);
  const contaminated = pickOrder('in_transit', { truck: 3 });
  const wrongMaterial = pickOrder('completed', { material: 'mixed_paper' });
  const keyed = pickOrder('completed', { truck: 1 });
  const soaked = pickOrder('completed', { material: 'occ' }, 2);
  const smallScale = pickOrder('completed', { material: 'aluminium_cans' });
  const jeddahRollback = pickOrder('completed', { city: 'Jeddah' });
  const duplicate = pickOrder('completed', { truck: 6 });
  const petCheck = pickOrder('completed', { material: 'pet' }, 1);
  const contamCancel = pickOrder('completed', { city: 'Riyadh' }, 4);
  const jeddahScale = pickOrder('completed', { city: 'Jeddah' }, 2);
  const training = pickOrder('in_transit', { city: 'Jeddah' });
  const sulayTo = round5(sulayScale.kg * 1.08);
  const jeddahTo = round5(jeddahScale.kg * 0.95);
  return [
    {
      action: 'user.create', entityId: ids.user(951), status: 'pending', by: M[1], hoursAgo: 30,
      payload: { username: 'w.alharthy@riyadhgreen.example', first_name: 'Waleed', last_name: 'Al-Harthy', gender: 'Male', nationality: 'Saudi Arabia', phone: '+966500030188', role: 'staff' },
      justification: 'Second driver for the Jeddah coastal loop while Sajid is on annual leave.',
      steps: [{ perm: perm(R, 'user', 'write'), status: 'pending' }],
    },
    {
      action: 'pickup_order.update_sensitive', entityId: sulayScale.id, status: 'pending', by: M[1], hoursAgo: 16,
      payload: { ...sulayScale.payload, corrected_amount: sulayTo },
      justification: `Sulay gate scale reads ${kgText(sulayTo)} of ${sulayScale.material} for ${sulayScale.reference} (${sulayScale.site}); the handheld captured ${kgText(sulayScale.kg)}.`,
      steps: [
        { perm: perm(R, 'pickup_order', 'write'), status: 'approved', by: M[0], notes: 'Scale ticket verified.', afterH: 2 },
        { perm: perm(R, 'approval', 'write'), status: 'pending' },
      ],
    },
    {
      action: 'pickup_order.reject', entityId: contaminated.id, status: 'pending', by: D[2], hoursAgo: 3,
      payload: contaminated.payload,
      justification: `The ${contaminated.material} on ${contaminated.reference} from ${contaminated.site} is contaminated with food waste — reject at the plant instead of sorting it.`,
    },
    {
      action: 'pickup_order.update_sensitive', entityId: wrongMaterial.id, status: 'pending', by: me, hoursAgo: 8,
      payload: { ...wrongMaterial.payload, corrected_material: 'OCC Cardboard' },
      justification: `${wrongMaterial.reference} from ${wrongMaterial.site} was recorded as ${wrongMaterial.material} but the load was clean OCC Cardboard; the price must follow the cardboard agreement.`,
    },
    {
      action: 'user.create', entityId: D[9], status: 'executed', by: M[0], hoursAgo: 24 * 38, resolvedAfterH: 4,
      payload: { username: DEMO_DRIVERS[9].email, first_name: 'Shafiq', last_name: 'Rahman', role: 'staff' },
      justification: 'New driver for truck HAV 9036 in Jeddah.',
      steps: [{ perm: perm(R, 'user', 'write'), status: 'approved', by: me, afterH: 4 }],
    },
    {
      action: 'pickup_order.update_sensitive', entityId: keyed.id, status: 'executed', by: D[0], hoursAgo: 24 * 8, resolvedBy: M[0], resolvedAfterH: 6,
      payload: { ...keyed.payload, corrected_amount: keyed.kg },
      justification: `I keyed ${kgText(keyed.kg / 10)} on ${keyed.reference}; the bale tag for the ${keyed.material} reads ${kgText(keyed.kg)}.`,
      steps: [
        { perm: perm(R, 'pickup_order', 'write'), status: 'approved', by: M[1], afterH: 1 },
        { perm: perm(R, 'approval', 'write'), status: 'approved', by: M[0], afterH: 6 },
      ],
    },
    {
      action: 'pickup_order.reject', entityId: soaked.id, status: 'executed', by: D[1], hoursAgo: 24 * 19, resolvedBy: M[0], resolvedAfterH: 3,
      payload: soaked.payload,
      justification: `The ${soaked.material} from ${soaked.site} (${soaked.reference}) was soaked after rain at the loading bay; moisture over 20 %.`,
      steps: [{ perm: perm(R, 'approval', 'write'), status: 'approved', by: M[0], notes: 'Supplier informed with photos.', afterH: 3 }],
    },
    {
      action: 'pickup_order.update_sensitive', entityId: smallScale.id, status: 'executed', mode: 'justified', by: M[0], hoursAgo: 24 * 5, resolvedAfterH: 0.1,
      payload: { ...smallScale.payload, corrected_amount: smallScale.kg },
      justification: `Manager correction: the ${smallScale.material} load on ${smallScale.reference} was weighed on the small scale (${kgText(smallScale.kg)}).`,
    },
    {
      action: 'pickup_order.rollback_after_pickup', entityId: jeddahRollback.id, status: 'approved', by: D[6], hoursAgo: 24 * 3, resolvedAfterH: 2,
      payload: jeddahRollback.payload,
      justification: `${jeddahRollback.reference} was closed at the wrong collection point in ${jeddahRollback.city} (${jeddahRollback.site}); reopen and reassign.`,
      steps: [{ perm: perm(R, 'approval', 'write'), status: 'approved', by: me, afterH: 2 }],
    },
    {
      action: 'pickup_order.delete', entityId: duplicate.id, status: 'rejected', by: D[4], hoursAgo: 24 * 14, resolvedBy: M[1], resolvedAfterH: 5,
      payload: duplicate.payload,
      rejection: 'A weighed delivery cannot be deleted; it is already invoiced to the supplier.',
      justification: `${duplicate.reference} (${duplicate.material}, ${duplicate.site}) appears twice on my run sheet.`,
      steps: [{ perm: perm(R, 'approval', 'write'), status: 'rejected', by: M[1], afterH: 5 }],
    },
    {
      action: 'user.create', entityId: ids.user(952), status: 'rejected', by: D[0], hoursAgo: 24 * 25, resolvedAfterH: 12,
      payload: { username: 'helper.ruh@riyadhgreen.example', first_name: 'Loading', last_name: 'Helper', role: 'staff' },
      rejection: 'Loading helpers do not need app access; they ride with a registered driver.',
      justification: 'Helper for heavy cardboard loads on the north loop.',
      steps: [{ perm: perm(R, 'user', 'write'), status: 'rejected', by: me, afterH: 12 }],
    },
    {
      action: 'pickup_order.update_sensitive', entityId: petCheck.id, status: 'cancelled', by: D[3], hoursAgo: 24 * 10, resolvedBy: D[3], resolvedAfterH: 1,
      payload: { ...petCheck.payload, corrected_amount: round5(petCheck.kg * 1.1) },
      justification: `Thought the ${petCheck.material} weight on ${petCheck.reference} was wrong — rechecked, it was right.`,
    },
    {
      action: 'pickup_order.reject', entityId: contamCancel.id, status: 'cancelled', by: me, hoursAgo: 24 * 29, resolvedBy: me, resolvedAfterH: 3,
      payload: contamCancel.payload,
      justification: `Contamination suspected in the ${contamCancel.material} from ${contamCancel.site} (${contamCancel.reference}); resolved with the supplier by phone.`,
    },
    {
      action: 'pickup_order.update_sensitive', entityId: jeddahScale.id, status: 'executed', by: D[6], hoursAgo: 24 * 47, resolvedBy: M[1], resolvedAfterH: 8,
      payload: { ...jeddahScale.payload, corrected_amount: jeddahTo },
      justification: `Jeddah Transfer Station scale reading (${kgText(jeddahTo)} of ${jeddahScale.material}) added to ${jeddahScale.reference} after the run closed.`,
      steps: [{ perm: perm(R, 'approval', 'write'), status: 'approved', by: M[1], afterH: 8 }],
    },
    {
      action: 'pickup_order.delete', entityId: training.id, status: 'executed', mode: 'justified', by: M[0], hoursAgo: 24 * 58, resolvedAfterH: 0.1,
      payload: training.payload,
      justification: `Training order (${training.reference}) created while onboarding the ${training.city} team.`,
    },
  ];
}

let approvals: ApprovalRequestSchema[] = [];
let createdCount = 0;

function seedAll(): ApprovalRequestSchema[] {
  return [
    ...outletSeeds().map((s, i) => build(DEMO_OUTLET_ORG_ID, i + 1, s)),
    ...recyclerSeeds().map((s, i) => build(DEMO_RECYCLER_ORG_ID, 101 + i, s)),
  ];
}

export function resetApprovals(): void {
  approvals = seedAll();
  createdCount = 0;
}

resetApprovals();

export function approvalsForOrg(orgId: string): ApprovalRequestSchema[] {
  return approvals.filter((a) => a.organization_id === orgId);
}

export function findApproval(id: string): ApprovalRequestSchema | undefined {
  return approvals.find((a) => a.id === id);
}

export function listApprovals(orgId: string, query: Record<string, unknown>): { result_list: ApprovalRequestSchema[]; total_count: number } {
  const str = (k: string) => (typeof query[k] === 'string' && query[k] !== '' ? (query[k] as string) : undefined);
  const status = str('status');
  const mode = str('mode');
  const action = str('action_type');
  const requestedBy = str('requested_by');
  const list = approvalsForOrg(orgId)
    .filter((a) => (!status || a.status === status) && (!mode || a.mode === mode) && (!action || a.action_type === action) && (!requestedBy || a.requested_by === requestedBy))
    .sort((a, b) => (b.requested_at ?? '').localeCompare(a.requested_at ?? ''));
  const offset = Math.max(0, Number(query.offset ?? 0) || 0);
  const limit = Math.max(1, Number(query.limit ?? 50) || 50);
  return { result_list: list.slice(offset, offset + limit), total_count: list.length };
}

function save(next: ApprovalRequestSchema): ApprovalRequestSchema {
  approvals = approvals.map((a) => (a.id === next.id ? next : a));
  return next;
}

function pendingOrThrow(id: string): ApprovalRequestSchema {
  const a = findApproval(id);
  if (!a) throw new DemoApprovalError(404, 'Approval request not found');
  if (a.status !== 'pending') throw new DemoApprovalError(400, 'This request is no longer pending');
  if (a.mode !== 'requires_approval') throw new DemoApprovalError(400, 'This request does not need a review');
  return a;
}

export function approveApproval(id: string, actorId: string, notes?: string): ApprovalRequestSchema {
  const a = pendingOrThrow(id);
  if (a.requested_by === actorId) throw new DemoApprovalError(403, 'You cannot approve your own request');
  const now = iso(new Date());
  const current = a.steps.find((s) => s.status === 'pending');
  const steps = a.steps.map((s) =>
    s === current
      ? { ...s, status: 'approved' as const, acted_by: actorId, acted_by_username: usernameOf(actorId), acted_at: now, notes: notes?.trim() || null }
      : s,
  );
  const nextStep = steps.find((s) => s.status === 'pending');
  if (nextStep) {
    return save({ ...a, steps, current_step_order: nextStep.step_order, updated_at: now });
  }
  let next: ApprovalRequestSchema = {
    ...a,
    steps,
    status: 'approved',
    resolved_by: actorId,
    resolved_by_username: usernameOf(actorId),
    resolved_at: now,
    updated_at: now,
  };
  if (a.action_type === 'user.create' && a.organization_id) {
    const created = addApprovedUser(a.organization_id, a.payload);
    if (created) {
      next = { ...next, status: 'executed', executed_at: now, entity_id: created.id ?? a.entity_id };
    }
  }
  return save(next);
}

export function rejectApproval(id: string, actorId: string, reason: string): ApprovalRequestSchema {
  const a = pendingOrThrow(id);
  if (!reason?.trim()) throw new DemoApprovalError(400, 'A rejection reason is required');
  if (a.requested_by === actorId) throw new DemoApprovalError(403, 'You cannot reject your own request');
  const now = iso(new Date());
  const current = a.steps.find((s) => s.status === 'pending');
  return save({
    ...a,
    steps: a.steps.map((s) =>
      s === current ? { ...s, status: 'rejected' as const, acted_by: actorId, acted_by_username: usernameOf(actorId), acted_at: now } : s,
    ),
    status: 'rejected',
    rejection_reason: reason.trim(),
    resolved_by: actorId,
    resolved_by_username: usernameOf(actorId),
    resolved_at: now,
    updated_at: now,
  });
}

export function cancelApproval(id: string, actorId: string): ApprovalRequestSchema {
  const a = pendingOrThrow(id);
  if (a.requested_by !== actorId) throw new DemoApprovalError(403, 'Only the requester can cancel this request');
  const now = iso(new Date());
  return save({
    ...a,
    status: 'cancelled',
    resolved_by: actorId,
    resolved_by_username: usernameOf(actorId),
    resolved_at: now,
    updated_at: now,
  });
}

export function submitApproval(orgId: string, actorId: string, body: SubmitApprovalPayload): ApprovalRequestSchema {
  if (!body?.action_type || !body.justification?.trim()) {
    throw new DemoApprovalError(400, 'action_type and justification are required');
  }
  createdCount += 1;
  const created = build(orgId, 500 + createdCount, {
    action: body.action_type,
    entityId: body.entity_id || ids.approval(900 + createdCount),
    payload: body.payload ?? {},
    justification: body.justification.trim(),
    by: actorId,
    hoursAgo: 0,
    status: 'pending',
  });
  approvals = [created, ...approvals];
  return created;
}
